import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { FieldArray, Formik } from "formik";
import { useSnackbar } from "notistack";
import React from "react";
import { StyledTextField } from "../../pages/login";
import { featuredLinksSchema } from "../../schema";
import { useUpdateFeaturedLinksMutation } from "../../services/profileAPI";
import { CustomDialogTitle } from "../../ui";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";

type EditFeaturedLinksDialogPropsType = {
  featuredLinks: string[];
  onClose: () => void;
};

function EditFeaturedLinksDialog({
  featuredLinks,
  onClose,
}: EditFeaturedLinksDialogPropsType) {
  const [updateFeaturedLinks] = useUpdateFeaturedLinksMutation();
  const { enqueueSnackbar } = useSnackbar();

  return (
    <>
      <CustomDialogTitle onClose={onClose}>
        <Typography>edit featured links</Typography>
      </CustomDialogTitle>

      <Formik
        initialValues={{
          featuredLinks: featuredLinks?.length > 0 ? featuredLinks : [""],
        }}
        validationSchema={featuredLinksSchema}
        onSubmit={async (values) => {
          try {
            const updatedFeaturedLinks = values.featuredLinks;
            await updateFeaturedLinks(updatedFeaturedLinks).unwrap();
            enqueueSnackbar("updated your links", {
              variant: "success",
            });
            onClose();
          } catch (err) {
            enqueueSnackbar("failed to update your links", {
              variant: "error",
            });
          }
        }}
      >
        {({
          values,
          handleSubmit,
          isSubmitting,
          handleChange,
          errors,
          touched,
        }) => (
          <>
            <DialogContent dividers>
              <FieldArray
                name="featuredLinks"
                render={(arrayHelpers) => (
                  <>
                    {values.featuredLinks?.map((link, index) => {
                      const error =
                        errors &&
                        errors.featuredLinks &&
                        errors.featuredLinks[index];
                      const hasTouched =
                        touched &&
                        touched.featuredLinks &&
                        touched.featuredLinks[index];
                      return (
                        <Stack
                          direction="row"
                          alignItems="center"
                          width="100%"
                          key={index}
                          mb={1}
                        >
                          <StyledTextField
                            label=""
                            fullWidth
                            placeholder="https://www.example.com"
                            value={link}
                            onChange={handleChange(`featuredLinks.${index}`)}
                            error={hasTouched && error}
                            helperText={hasTouched && error}
                          />
                          <IconButton
                            onClick={() => arrayHelpers.remove(index)}
                          >
                            <CancelOutlinedIcon />
                          </IconButton>
                        </Stack>
                      );
                    })}

                    {values?.featuredLinks?.length < 3 ? (
                      <Stack direction="row" justifyContent="center">
                        <Button
                          variant="contained"
                          color="secondary"
                          onClick={() => arrayHelpers.push("")}
                          sx={{ fontSize: 10 }}
                        >
                          add link
                        </Button>
                      </Stack>
                    ) : null}
                  </>
                )}
              />
            </DialogContent>

            <DialogActions>
              <Button onClick={onClose} color="secondary">
                cancel
              </Button>
              <LoadingButton
                variant="text"
                type="submit"
                loading={isSubmitting}
                onClick={handleSubmit}
              >
                update
              </LoadingButton>
            </DialogActions>
          </>
        )}
      </Formik>
    </>
  );
}

export default EditFeaturedLinksDialog;
